import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, CardHeader, Divider, Stack, Typography } from "@mui/material";
import MuiCard, { CardProps } from "@mui/material/Card";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { InputText } from "primereact/inputtext";
import {
  NewReusableCustomQuestionGroup,
  ReusableCustomQuestionGroup,
} from "../../../shared/interfaces/ReusableCustomQuestionGroup";
import { ProgressButton } from "../../../shared/ProgressButton";
import { CancelButton } from "../../../shared/CancelButton";
import { ReusableQuestionFieldSelectionDialog } from "../QuestionFields/ReusableQuestionFieldSelectionDialog";
import { useCustomQuestionGroupCreate } from "../../../data/CustomWorkflowQuestion/useCustomQuestionGroupCreate";
import { ReusableCustomQuestionField } from "../../../shared/interfaces/ReusableCustomQuestionField";
import { ReusableCustomQuestionFields } from "../QuestionFields/ReusableCustomQuestionFields";

interface Props {
  customerId: number;
  canEditWorkflow?: boolean;
  onCreateGroupSuccess: (successMessage: string) => void;
  onCreateGroupError: (errorMessage: string) => void;
  onCancelButtonClick: () => void;
}

export function NewCustomQuestionGroupCard(props: Props & CardProps) {
  const { t } = useTranslation();
  const { customerId, canEditWorkflow, onCreateGroupSuccess, onCreateGroupError, onCancelButtonClick, ...cardProps } =
    props;

  const [editedCustomQuestionGroup, setEditedCustomQuestionGroup] = useState<Partial<ReusableCustomQuestionGroup>>({});
  const [questionSelectionDialogOpen, setQuestionSelectionDialogOpen] = useState(false);

  const customQuestionGroupCreate = useCustomQuestionGroupCreate({
    onSuccess: handleQuestionGroupCreateSuccess,
    onError: handleQuestionGroupCreateError,
  });

  function handleCreateGroupButtonClick() {
    const editedQuestionGroup: NewReusableCustomQuestionGroup = {
      customerId: customerId,
      groupName: editedCustomQuestionGroup.groupName || "",
      questionIdList: editedCustomQuestionGroup.questionList?.map((question) => question.id) || [],
    };

    customQuestionGroupCreate.mutate(editedQuestionGroup);
  }

  function handleQuestionGroupCreateSuccess() {
    onCreateGroupSuccess(
      t("customQuestionLibrary.libraryItemAdded", {
        val: t("customQuestions.questionGroup"),
      }),
    );
    setEditedCustomQuestionGroup({});
  }

  function handleQuestionGroupCreateError() {
    onCreateGroupError(t("common.dataCreateError", { val: t("customQuestions.questionGroup") }));
  }

  function handleCancelEditGroupButtonClick() {
    setEditedCustomQuestionGroup({});
    onCancelButtonClick();
  }

  function handleQuestionGroupNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    setEditedCustomQuestionGroup((prevState) => ({
      ...prevState,
      groupName: e.target.value,
    }));
  }

  function handleQuestionListChange(selectedQuestions: ReusableCustomQuestionField[]) {
    setEditedCustomQuestionGroup((prevState) => ({
      ...prevState,
      questionList: selectedQuestions,
    }));
  }

  return (
    <>
      <MuiCard variant="outlined" {...cardProps}>
        <CardHeader
          title={
            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography variant="subtitle2" color="textSecondary" lineHeight="3.3em">
                {t("customQuestions.questionGroup")}:
              </Typography>
              {
                <Stack>
                  <InputText
                    placeholder={t("customQuestions.questionGroupName")}
                    value={editedCustomQuestionGroup.groupName || ""}
                    onChange={handleQuestionGroupNameChange}
                  />
                </Stack>
              }
            </Stack>
          }
          action={
            <>
              <ProgressButton loading={customQuestionGroupCreate.isLoading} onClick={handleCreateGroupButtonClick}>
                {t("common.save")}
              </ProgressButton>
              <CancelButton onClick={handleCancelEditGroupButtonClick} disabled={customQuestionGroupCreate.isLoading} />
            </>
          }
        />
        <Divider />
        <Box display="flex" justifyContent="center" m={1}>
          <Button
            onClick={() => setQuestionSelectionDialogOpen(true)}
            startIcon={<LibraryBooksIcon />}
            sx={{ textTransform: "uppercase" }}
          >
            {editedCustomQuestionGroup.questionList && editedCustomQuestionGroup.questionList?.length > 0
              ? t("customQuestionLibrary.editQuestionList")
              : t("customQuestionLibrary.addQuestions")}
          </Button>
        </Box>
        <ReusableCustomQuestionFields
          customQuestionFields={editedCustomQuestionGroup.questionList}
          customerId={customerId}
          displayMode="view"
          containerProps={{ variant: "elevation" }}
          canEditWorkflow={canEditWorkflow}
        />
      </MuiCard>
      {questionSelectionDialogOpen && (
        <ReusableQuestionFieldSelectionDialog
          customerId={customerId}
          selectedQuestions={editedCustomQuestionGroup.questionList}
          onApplySelection={handleQuestionListChange}
          open={questionSelectionDialogOpen}
          onClose={() => setQuestionSelectionDialogOpen(false)}
          canEditWorkflow={canEditWorkflow}
        />
      )}
    </>
  );
}
